/**
 * The system's one publish gate.
 *
 * `m08-admin.js` says it in its own header: «v0.5: sistemdeki TEK publish kapısı buradadır». The
 * wizard, the asset library and the template list all produce drafts and send the user here; a
 * `TemplateRelease` leaves the system through this checklist and nowhere else. So this file is a
 * port of `releaseChecklist` and `publishRelease` (`m02-mockdb.js:285-370`) plus the editor's own
 * `collectOverflow` (`m08-admin.js:138`), and nothing about React.
 *
 * It carries more than the ten checklist rows because the reference's `schema` row is the entry
 * point to the whole fail-closed validator chain — `validateFieldSchema` (`m01c-validate.js:207`)
 * over `validateLayoutSchemaStrict` (`m01e-schema.js:354`) over the validation-config, default-
 * value, URL-policy and strict-date validators. The editor's field tab edits every input those
 * rules read (bounds, pattern presets, default values, data types), so a gate that only checked
 * keys and visibility would let exactly the schemas back through that v0.7 was written to stop.
 * Nothing else in the rebuild carries that chain, so it lives here, next to its only caller.
 *
 * Two things the reference does that look like mistakes are reproduced on purpose, each marked at
 * its line: `collectOverflow`'s result is computed and never read, and `__previewChecked` is set
 * true only by the demo seed, so the editor's publish button cannot enable for a template the
 * seed published. Both are the frozen v0.7 artifact's behaviour.
 */
import constants from '../data/demo-constants.json';
import type { DemoFieldDefinition, DemoRelease, DemoSettings, DemoTemplate } from '../data/types';
import type { EditorLayout } from '../screens/editor/contract';
import { asRenderLayout } from './editor-layout';
import { FIELD_DATA_TYPES, PUBLICATION_POLICIES, trLower, trUpper } from './reference';
import {
  canonicalJSON,
  canvasH,
  canvasW,
  holderFieldKey,
  layoutFonts,
  type PlanFailure,
  type RenderData,
  resolveAssetRef,
  resolveRenderPlan,
  type SampleMode,
  sampleData,
} from './render';
import { allFonts, type RenderFont } from './render/library';
import { prototypeNowUTC } from './template-list';

const RELEASE_SCHEMA_VERSION = constants.RELEASE_SCHEMA_VERSION;

// ------------------------------------------------------------------- §the visibility contract

/** `VISIBILITY` — `m01c-validate.js:9`. */
const VISIBILITY = ['private', 'public_metadata', 'public_render', 'public_metadata_and_render'];
const canRender = (v: string): boolean => v === 'public_render' || v === 'public_metadata_and_render';
const canPublicMetadata = (v: string): boolean =>
  v === 'public_metadata' || v === 'public_metadata_and_render';

/** `VISIBILITY_TR` — `m01c-validate.js:10`; the same four labels the field panel shows. */
const VISIBILITY_TR: Record<string, string> = {
  private: 'Gizli (yalnız yönetim)',
  public_metadata: 'Public detay listesi',
  public_render: 'Yalnız görsel',
  public_metadata_and_render: 'Detay listesi + görsel',
};

/** System fields are always private; no template may make one public (`m01c-validate.js:13`). */
const SYSTEM_PRIVATE_KEYS = [
  'email',
  'claim_token',
  'claim_token_hash',
  'internal_id',
  'batch_id',
  'admin_actor',
  'source_row_hash',
  'private_contact_email',
];
const isSystemPrivate = (key: string): boolean => SYSTEM_PRIVATE_KEYS.includes(key);

/**
 * §2 — there is no recipient-specific image or signature claim in v1: graphics are template asset
 * layers only. The names are kept so a release that still carries one is rejected rather than
 * silently skipped (`m01c-validate.js:38`).
 */
const REMOVED_DYNAMIC_DATA_TYPES = ['image', 'signature'];
const SCALAR_DATA_TYPES = FIELD_DATA_TYPES.map((x) => x.key).filter((k) => k !== 'qr');
const isImageDataType = (t: string): boolean => REMOVED_DYNAMIC_DATA_TYPES.includes(t);
const isScalarDataType = (t: string): boolean => SCALAR_DATA_TYPES.includes(t);

/** `HOLDER_ROLES` — `m01c-validate.js:178`. */
const HOLDER_ROLES: Record<'person' | 'organization', string[]> = {
  person: ['subject_person', 'recipient_name'],
  organization: ['subject_org', 'organization_name'],
};

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const fieldLabelOf = (f: FieldLike, lang: 'tr' | 'en'): string =>
  (f.labels ? (lang === 'en' ? f.labels.en : f.labels.tr) : null) || f.key;

/** The shape the validators read. Wider than `DemoFieldDefinition` because they are fail-closed:
    a field that is not a field at all has to reach them, not be excluded by a type. */
interface FieldLike {
  id?: unknown;
  key: string;
  labels?: { tr?: string; en?: string } | null;
  dataType?: string;
  required?: boolean;
  auto?: boolean;
  importable?: boolean;
  visibility?: string;
  defaultValue?: unknown;
  normalization?: unknown;
  validation?: unknown;
  sample?: string;
  role?: string | null;
}

const asField = (f: DemoFieldDefinition): FieldLike => f as unknown as FieldLike;

// ------------------------------------------------------------------------------ §normalizasyon

/** `NORMALIZERS` — `m01c-validate.js:52`. */
const NORMALIZERS: Record<string, (v: unknown) => string> = {
  none: (v) => String(v ?? ''),
  trim: (v) => String(v ?? '').trim(),
  collapse_spaces: (v) =>
    String(v ?? '')
      .replace(/\s+/g, ' ')
      .trim(),
  upper_tr: (v) => trUpper(String(v ?? '').trim()),
  lower_tr: (v) => trLower(String(v ?? '').trim()),
  title_tr: (v) =>
    String(v ?? '')
      .trim()
      .split(/\s+/)
      .map((w) => (w ? trUpper(w.slice(0, 1)) + trLower(w.slice(1)) : w))
      .join(' '),
};

/** Every value is collapsed first, then run through the field's own list (`m01c-validate.js:60`). */
function normalizeValue(def: { normalization?: unknown } | null, raw: unknown): string {
  const own = def?.normalization;
  const list = ['collapse_spaces'].concat(
    own === undefined || own === null ? [] : Array.isArray(own) ? own.map(String) : [String(own)],
  );
  let v: unknown = raw;
  for (const n of list) {
    const fn = NORMALIZERS[n];
    if (fn) v = fn(v);
  }
  return String(v ?? '');
}

// ------------------------------------------------------------------ §3 strict date engine (m01d)

const DATE_ERROR_TR: Record<string, string> = {
  empty: 'Tarih boş.',
  format: 'Tarih biçimi tanınmadı. YYYY-AA-GG ya da GG.AA.YYYY kullanın.',
  not_a_calendar_date: 'Bu takvimde olmayan bir tarih (gün/ay geçersiz).',
  month_range: 'Ay 1–12 aralığında olmalı.',
  day_range: 'Gün, seçilen ay için geçerli aralıkta olmalı.',
  year_range: 'Yıl 1900–2200 aralığında olmalı.',
  datetime_format:
    'Tarih-saat yalnız zaman dilimi taşıyan RFC3339 biçiminde kabul edilir (ör. 2026-09-02T10:00:00Z).',
  datetime_invalid: 'Tarih-saat değeri geçerli bir an değil.',
};

const MIN_YEAR = 1900;
const MAX_YEAR = 2200;

type DateParse = { ok: true; value: string } | { ok: false; code: string; message: string };

const dErr = (code: string): DateParse => ({
  ok: false,
  code,
  message: DATE_ERROR_TR[code] ?? (DATE_ERROR_TR.format as string),
});

const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

function daysInMonth(year: number, month: number): number {
  if (!(month >= 1 && month <= 12)) return 0;
  return [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] as number;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * `parseDateOnlyStrict` — `m01d-dates.js:40`. The component round-trip is the point: `Date`
 * silently rolls 2026-02-31 forward to 2026-03-03, and this is where that is caught.
 */
function parseDateOnlyStrict(value: unknown): DateParse {
  if (value === null || value === undefined || String(value).trim() === '') return dErr('empty');
  let s = String(value).trim();
  const isoT = /^(\d{4})-(\d{2})-(\d{2})[T ]/.exec(s);
  if (isoT) s = `${isoT[1]}-${isoT[2]}-${isoT[3]}`;
  let y: number;
  let m: number;
  let d: number;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) {
    y = +(iso[1] as string);
    m = +(iso[2] as string);
    d = +(iso[3] as string);
  } else {
    /* TR biçimi: 31.12.2026 · 31/12/2026 · 31-12-2026 */
    const tr = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(s);
    if (!tr) return dErr('format');
    d = +(tr[1] as string);
    m = +(tr[2] as string);
    y = +(tr[3] as string);
  }
  if (!(y >= MIN_YEAR && y <= MAX_YEAR)) return dErr('year_range');
  if (!(m >= 1 && m <= 12)) return dErr('month_range');
  const dim = daysInMonth(y, m);
  if (!(d >= 1 && d <= dim)) return dErr(d >= 1 && d <= 31 ? 'not_a_calendar_date' : 'day_range');
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() + 1 !== m || probe.getUTCDate() !== d)
    return dErr('not_a_calendar_date');
  return { ok: true, value: `${y}-${pad2(m)}-${pad2(d)}` };
}

/** `parseDateTimeStrict` — `m01d-dates.js:66`. The timezone is mandatory; naive input is refused. */
function parseDateTimeStrict(value: unknown): DateParse {
  if (value === null || value === undefined || String(value).trim() === '') return dErr('empty');
  const s = String(value).trim();
  const m =
    /^(\d{4})-(\d{2})-(\d{2})[Tt ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|z|[+-]\d{2}:\d{2})$/.exec(s);
  if (!m) return dErr('datetime_format');
  const dOnly = parseDateOnlyStrict(`${m[1]}-${m[2]}-${m[3]}`);
  if (!dOnly.ok) return dOnly;
  const hh = +(m[4] as string);
  const mi = +(m[5] as string);
  const ss = m[6] ? +m[6] : 0;
  if (hh > 23 || mi > 59 || ss > 60) return dErr('datetime_invalid');
  const offset = m[8] as string;
  if (offset !== 'Z' && offset !== 'z') {
    const om = /^([+-])(\d{2}):(\d{2})$/.exec(offset);
    if (!om || +(om[2] as string) > 14 || +(om[3] as string) > 59) return dErr('datetime_format');
  }
  const ms = Date.parse(s.replace(' ', 'T'));
  if (!Number.isFinite(ms)) return dErr('datetime_invalid');
  return { ok: true, value: new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z') };
}

// --------------------------------------------------- §10 URL policies (a parser, not a regex)

const URL_MAX_LENGTH = 2048;

interface UrlPolicy {
  schemes: string[];
  requireHttps: boolean;
  allowLoopback: boolean;
  allowPrivate: boolean;
  hostAllowlist?: string[];
  pathPrefix?: string;
}

/** `URL_POLICIES` — `m01e-schema.js:45`. */
const URL_POLICIES: Record<string, UrlPolicy> = {
  external: { schemes: ['http:', 'https:'], requireHttps: false, allowLoopback: false, allowPrivate: false },
  public_credential: {
    schemes: ['https:'],
    requireHttps: true,
    allowLoopback: false,
    allowPrivate: false,
  },
  issuer: { schemes: ['https:'], requireHttps: true, allowLoopback: false, allowPrivate: false },
  linkedin_page: {
    schemes: ['https:'],
    requireHttps: true,
    allowLoopback: false,
    allowPrivate: false,
    hostAllowlist: ['linkedin.com', 'www.linkedin.com'],
    pathPrefix: '/company/',
  },
  dev: { schemes: ['http:', 'https:'], requireHttps: false, allowLoopback: true, allowPrivate: true },
};

const URL_ERROR_TR: Record<string, string> = {
  url_empty: 'Adres boş.',
  url_too_long: `Adres çok uzun (en çok ${URL_MAX_LENGTH} karakter).`,
  url_control_chars: 'Adres boşluk veya kontrol karakteri içeriyor.',
  url_unparseable: 'Adres çözümlenemedi.',
  url_scheme_not_allowed: 'Yalnız http:// ve https:// adresleri kabul edilir.',
  url_https_required: 'Bu alan için https:// zorunludur.',
  url_credentials_forbidden: 'Adres kullanıcı adı/parola içeremez.',
  url_loopback_forbidden: 'Loopback (localhost / 127.0.0.1) adresi kabul edilmez.',
  url_private_host_forbidden: 'Özel ağ (private / link-local) adresi kabul edilmez.',
  url_host_not_allowed: 'Bu alan için adres güvenilen alan adında değil.',
  url_path_not_allowed: 'Adres beklenen yol ile başlamıyor.',
  url_host_invalid: 'Adresin alan adı geçersiz.',
};

const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '::1', '0.0.0.0', '[::1]'];

function isPrivateHost(host: string): boolean {
  const h = String(host || '')
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  if (LOOPBACK_HOSTS.includes(h)) return true;
  if (/\.local(host)?$/.test(h) || h === 'local') return true;
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(h);
  if (m) {
    const a = +(m[1] as string);
    const b = +(m[2] as string);
    if ([a, b, +(m[3] as string), +(m[4] as string)].some((n) => n > 255)) return true;
    if (a === 10 || a === 127 || a === 0) return true;
    if (a === 192 && b === 168) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 169 && b === 254) return true;
    if (a === 100 && b >= 64 && b <= 127) return true;
  }
  if (/^f[cd][0-9a-f]{2}:/i.test(h) || /^fe80:/i.test(h)) return true;
  return false;
}

type UrlResult = { ok: true; value: string } | { ok: false; code: string; message: string };

/** `validateUrlValue` — `m01e-schema.js:96`. */
function validateUrlValue(raw: unknown, policyName = 'external'): UrlResult {
  const pol = URL_POLICIES[policyName] ?? (URL_POLICIES.external as UrlPolicy);
  const err = (code: string): UrlResult => ({ ok: false, code, message: URL_ERROR_TR[code] ?? code });
  const s = String(raw ?? '');
  if (!s.trim()) return err('url_empty');
  if (s.length > URL_MAX_LENGTH) return err('url_too_long');
  /* A parser quietly trims some of these; this refuses them instead. */
  // biome-ignore lint/suspicious/noControlCharactersInRegex: refusing control characters is the rule itself (m01e-schema.js:105)
  if (/\s/.test(s) || /[\u0000-\u001F\u007F-\u009F]/.test(s)) return err('url_control_chars');
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return err('url_unparseable');
  }
  if (!pol.schemes.includes(u.protocol))
    return err(
      u.protocol === 'https:' || u.protocol === 'http:' ? 'url_https_required' : 'url_scheme_not_allowed',
    );
  if (pol.requireHttps && u.protocol !== 'https:') return err('url_https_required');
  if (u.username || u.password) return err('url_credentials_forbidden');
  if (!u.hostname || !/^[a-z0-9._~%-]+$|^\[[0-9a-f:.]+\]$/i.test(u.hostname)) return err('url_host_invalid');
  const host = u.hostname.toLowerCase();
  if (!pol.allowLoopback && LOOPBACK_HOSTS.includes(host)) return err('url_loopback_forbidden');
  if (!pol.allowPrivate && isPrivateHost(host)) return err('url_private_host_forbidden');
  if (pol.hostAllowlist && !pol.hostAllowlist.includes(host)) return err('url_host_not_allowed');
  if (pol.pathPrefix && !u.pathname.startsWith(pol.pathPrefix)) return err('url_path_not_allowed');
  return { ok: true, value: u.href };
}

// ------------------------------------------- §1.3 pattern presets (raw regex is out of scope)

interface PatternPreset {
  tr: string;
  test: (v: string) => boolean;
  hint: string;
}

/**
 * `PATTERN_PRESETS` — `m01e-schema.js:25`. Free user regex is deliberately not in v1: a broken one
 * threw inside `new RegExp`, the throw was swallowed, and the rule was then silently ignored.
 */
export const PATTERN_PRESETS: Record<string, PatternPreset> = {
  none: { tr: 'Kısıt yok', test: () => true, hint: '' },
  letters: {
    tr: 'Yalnız harf ve boşluk',
    test: (v) => /^[\p{L}\s'.-]+$/u.test(v),
    hint: 'Yalnız harf, boşluk, kesme ve tire',
  },
  alphanumeric: {
    tr: 'Harf ve rakam',
    test: (v) => /^[\p{L}\p{N}\s'.-]+$/u.test(v),
    hint: 'Harf, rakam, boşluk, kesme ve tire',
  },
  phone_tr: {
    tr: 'Telefon (TR)',
    test: (v) => /^(\+90|0)?\s?5\d{2}\s?\d{3}\s?\d{2}\s?\d{2}$/.test(String(v).replace(/[()-]/g, '')),
    hint: 'Örn. 0532 123 45 67',
  },
  employee_code: {
    tr: 'Personel kodu',
    test: (v) => /^[A-Z0-9][A-Z0-9_-]{1,31}$/i.test(v),
    hint: '2–32 karakter; harf, rakam, tire, alt çizgi',
  },
  slug: {
    tr: 'Slug',
    test: (v) => /^[a-z0-9]+(-[a-z0-9]+)*$/.test(v),
    hint: 'küçük-harf-ve-tire',
  },
};

const PATTERN_PRESET_KEYS = Object.keys(PATTERN_PRESETS);

// ------------------------------------------------------------------------ the single validator

interface ValueError {
  code: string;
  message: string;
  fromDefault?: boolean;
}

interface ValueResult {
  ok: boolean;
  value: string;
  errors: ValueError[];
  defaultApplied: boolean;
}

/**
 * `validateFieldValue` — `m01c-validate.js:70`.
 *
 * §1.5 — a default value no longer skips the pipeline. In v0.6 an empty value returned
 * `def.defaultValue` directly, so `javascript:alert(1)` reached a URL field and `2026-02-31` a
 * date field without ever being validated (V06-IND-002).
 */
export function validateFieldValue(
  def: FieldLike,
  rawValue: unknown,
  ctx: { defaultProbe?: boolean } = {},
): ValueResult {
  const errors: ValueError[] = [];
  const label = (def.labels && (def.labels.tr || def.labels.en)) || def.key;
  const V = (def.validation && typeof def.validation === 'object' ? def.validation : {}) as Record<
    string,
    unknown
  >;
  let value = normalizeValue(def, rawValue);
  let empty = value === '';
  const hasDefault =
    !ctx.defaultProbe &&
    def.defaultValue !== undefined &&
    def.defaultValue !== null &&
    String(def.defaultValue) !== '';
  let defaultApplied = false;
  if (empty && hasDefault) {
    value = normalizeValue(def, def.defaultValue);
    empty = value === '';
    defaultApplied = true;
  }
  if (empty) {
    if (def.required) errors.push({ code: 'required', message: `${label} zorunlu` });
    return { ok: !errors.length, value: '', errors, defaultApplied: false };
  }
  const t = def.dataType || 'string';
  const str = String(value);
  const num = (k: string): number | null => (typeof V[k] === 'number' ? (V[k] as number) : null);
  const bound = (k: string, alt: string): unknown => (V[k] !== undefined ? V[k] : V[alt]);

  if (t === 'email') {
    if (!EMAIL_RX.test(str)) errors.push({ code: 'email', message: `${label} geçerli bir e-posta değil` });
  } else if (t === 'url' || t === 'credential_url') {
    const policy = String(V.urlPolicy ?? (t === 'credential_url' ? 'public_credential' : 'external'));
    const r = validateUrlValue(str, policy);
    if (!r.ok) errors.push({ code: r.code, message: `${label}: ${r.message}` });
    else value = r.value;
  } else if (t === 'number' || t === 'integer') {
    const n = Number(String(str).replace(',', '.'));
    if (!Number.isFinite(n)) errors.push({ code: 'number', message: `${label} sayı değil` });
    else {
      if (t === 'integer' && !Number.isInteger(n))
        errors.push({ code: 'integer', message: `${label} tam sayı olmalı` });
      const mn = num('min');
      const mx = num('max');
      if (mn !== null && n < mn) errors.push({ code: 'min', message: `${label} en az ${mn} olmalı` });
      if (mx !== null && n > mx) errors.push({ code: 'max', message: `${label} en çok ${mx} olabilir` });
      value = String(n);
    }
  } else if (t === 'date' || t === 'datetime') {
    const r = t === 'date' ? parseDateOnlyStrict(str) : parseDateTimeStrict(str);
    if (!r.ok) errors.push({ code: r.code, message: `${label}: ${r.message}` });
    else {
      const dMin = bound('dateMin', 'min');
      const dMax = bound('dateMax', 'max');
      if (dMin && r.value < String(dMin))
        errors.push({ code: 'dateMin', message: `${label} ${String(dMin)} tarihinden önce olamaz` });
      if (dMax && r.value > String(dMax))
        errors.push({ code: 'dateMax', message: `${label} ${String(dMax)} tarihinden sonra olamaz` });
      value = r.value;
    }
  } else if (t === 'boolean') {
    const b = /^(1|true|evet|yes|e|y|x)$/i.test(str)
      ? 'true'
      : /^(0|false|hayır|hayir|no|h|n)$/i.test(str)
        ? 'false'
        : null;
    if (b === null) errors.push({ code: 'boolean', message: `${label} evet/hayır olarak okunamadı` });
    else value = b;
  } else if (t === 'enum') {
    const opts = (Array.isArray(V.enum) ? V.enum : []).map((x) => String(x));
    if (!opts.length)
      errors.push({ code: 'enumEmpty', message: `${label} için seçenek listesi tanımlı değil` });
    else {
      const hit = opts.find((o) => trLower(o) === trLower(str));
      if (!hit)
        errors.push({
          code: 'enum',
          message: `${label} şu seçeneklerden biri olmalı: ${opts.join(', ')}`,
        });
      else value = hit;
    }
  }

  if (!errors.length && (t === 'string' || t === 'text' || t === 'url' || t === 'enum')) {
    const minL = num('minLength');
    const maxL = num('maxLength');
    if (minL !== null && str.length < minL)
      errors.push({ code: 'minLength', message: `${label} en az ${minL} karakter olmalı` });
    if (maxL !== null && str.length > maxL)
      errors.push({ code: 'maxLength', message: `${label} en çok ${maxL} karakter olabilir` });
    if (V.pattern !== undefined)
      errors.push({
        code: 'unsupported_custom_regex',
        message: `${label} için ham regex desteklenmiyor; patternPreset kullanın`,
      });
    if (V.patternPreset && V.patternPreset !== 'none') {
      const preset = PATTERN_PRESETS[String(V.patternPreset)];
      if (!preset)
        errors.push({
          code: 'unknown_pattern_preset',
          message: `${label} için bilinmeyen biçim kuralı: ${String(V.patternPreset)}`,
        });
      else if (!preset.test(String(value)))
        errors.push({
          code: 'pattern',
          message: V.patternMessage
            ? String(V.patternMessage)
            : `${label} beklenen biçimde değil${preset.hint ? ` (${preset.hint})` : ''}`,
        });
    }
  }

  /* An applied default that fails its own rules is a SCHEMA fault: the user left it empty and the
     system supplied the invalid value. */
  if (defaultApplied && errors.length) {
    for (const e of errors) e.fromDefault = true;
    errors.unshift({
      code: 'invalid_default_value',
      fromDefault: true,
      message: `${label} için tanımlı varsayılan değer alan kurallarını geçmiyor; şablon düzeltilmeli`,
    });
  }
  return { ok: !errors.length, value, errors, defaultApplied };
}

// ---------------------------------------------- §1.2 / §1.4 the validation-config validator

export interface SchemaError {
  code: string;
  message: string;
  path: string;
}

/** `VALIDATION_PROPS_BY_TYPE` — `m01e-schema.js:118`. A rule the type cannot use is an error. */
const VALIDATION_PROPS_BY_TYPE: Record<string, string[]> = {
  string: ['minLength', 'maxLength', 'patternPreset', 'patternMessage'],
  text: ['minLength', 'maxLength', 'patternPreset', 'patternMessage'],
  email: ['maxLength'],
  url: ['maxLength', 'urlPolicy'],
  number: ['min', 'max'],
  integer: ['min', 'max'],
  date: ['dateMin', 'dateMax'],
  datetime: ['dateMin', 'dateMax'],
  boolean: [],
  enum: ['enum', 'minLength', 'maxLength'],
  credential_id: [],
  credential_url: ['urlPolicy'],
  qr: [],
};

const ENUM_LIMITS = { maxOptions: 200, maxItemLength: 200 };
const LABEL_MAX_LENGTH = 160;
const CANONICAL_ID_RX = /^[a-z][a-z0-9_]{1,63}$/;
const CANONICAL_KEY_RX = /^[a-z][a-z0-9_]*$/;
const isFiniteNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const isNonNegInt = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0;

/** `validateValidationConfig` — `m01e-schema.js:137`. */
export function validateValidationConfig(field: FieldLike): SchemaError[] {
  const errs: SchemaError[] = [];
  const key = field?.key || '(anahtarsız)';
  const add = (code: string, message: string): void => {
    errs.push({ code, message: `«${key}» ${message}`, path: `fieldDefinitions.${key}.validation` });
  };
  const raw = field?.validation;
  if (raw === undefined || raw === null) return errs;
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    add('validation_not_object', 'validation bir nesne olmalı.');
    return errs;
  }
  const V = raw as Record<string, unknown>;
  const t = field.dataType || 'string';
  const allowed = VALIDATION_PROPS_BY_TYPE[t];
  if (!allowed) {
    add('validation_unknown_datatype', `bilinmeyen dataType: ${t}`);
    return errs;
  }

  if (Object.hasOwn(V, 'pattern')) {
    add(
      'unsupported_custom_regex',
      'ham `validation.pattern` v1 kapsamında desteklenmiyor; yalnız `patternPreset` kullanılabilir.',
    );
  }
  for (const p of Object.keys(V)) {
    if (p === 'pattern') continue;
    if (!allowed.includes(p))
      add('validation_prop_not_applicable', `«${p}» kuralı ${t} türü için geçerli değil.`);
  }

  if (V.patternPreset !== undefined) {
    if (typeof V.patternPreset !== 'string' || !PATTERN_PRESET_KEYS.includes(V.patternPreset)) {
      add(
        'unknown_pattern_preset',
        `bilinmeyen patternPreset: ${String(V.patternPreset)} (izinli: ${PATTERN_PRESET_KEYS.join(', ')})`,
      );
    }
  }
  if (
    V.patternMessage !== undefined &&
    (typeof V.patternMessage !== 'string' || V.patternMessage.length > LABEL_MAX_LENGTH)
  ) {
    add('invalid_pattern_message', 'patternMessage string ve 160 karakterden kısa olmalı.');
  }

  const hasMinL = V.minLength !== undefined && V.minLength !== null;
  const hasMaxL = V.maxLength !== undefined && V.maxLength !== null;
  if (hasMinL && !isNonNegInt(V.minLength))
    add('invalid_min_length', 'minLength negatif olmayan tam sayı olmalı.');
  if (hasMaxL && !isNonNegInt(V.maxLength))
    add('invalid_max_length', 'maxLength negatif olmayan tam sayı olmalı.');
  if (
    hasMinL &&
    hasMaxL &&
    isNonNegInt(V.minLength) &&
    isNonNegInt(V.maxLength) &&
    V.minLength > V.maxLength
  ) {
    add('reversed_length_bounds', `minLength (${V.minLength}) maxLength'ten (${V.maxLength}) büyük olamaz.`);
  }

  const hasMin = V.min !== undefined && V.min !== null;
  const hasMax = V.max !== undefined && V.max !== null;
  if (hasMin && !isFiniteNum(V.min)) add('invalid_number_bound', 'min sonlu bir sayı olmalı.');
  if (hasMax && !isFiniteNum(V.max)) add('invalid_number_bound', 'max sonlu bir sayı olmalı.');
  if (hasMin && hasMax && isFiniteNum(V.min) && isFiniteNum(V.max) && V.min > V.max) {
    add('reversed_number_bounds', `min (${V.min}) max'tan (${V.max}) büyük olamaz.`);
  }

  const parseBound = (v: unknown): DateParse =>
    t === 'datetime' ? parseDateTimeStrict(v) : parseDateOnlyStrict(v);
  let pMin: DateParse | null = null;
  let pMax: DateParse | null = null;
  if (V.dateMin !== undefined && V.dateMin !== null && V.dateMin !== '') {
    pMin = parseBound(V.dateMin);
    if (!pMin.ok) add('invalid_date_bound', `dateMin geçerli bir tarih değil (${pMin.code}).`);
  }
  if (V.dateMax !== undefined && V.dateMax !== null && V.dateMax !== '') {
    pMax = parseBound(V.dateMax);
    if (!pMax.ok) add('invalid_date_bound', `dateMax geçerli bir tarih değil (${pMax.code}).`);
  }
  if (pMin?.ok && pMax?.ok && pMin.value > pMax.value) {
    add('reversed_date_bounds', `dateMin (${pMin.value}) dateMax'tan (${pMax.value}) sonra olamaz.`);
  }

  if (t === 'enum') {
    const list = V.enum;
    if (!Array.isArray(list) || list.length === 0) add('enum_empty', 'enum listesi boş olamaz.');
    else {
      if (list.length > ENUM_LIMITS.maxOptions)
        add('enum_too_many', `enum listesi en çok ${ENUM_LIMITS.maxOptions} seçenek taşıyabilir.`);
      const seen = new Set<string>();
      list.forEach((rawItem, i) => {
        if (typeof rawItem !== 'string' && typeof rawItem !== 'number') {
          add('enum_item_type', `enum[${i}] string ya da sayı olmalı.`);
          return;
        }
        const s = String(rawItem).trim();
        if (!s) {
          add('enum_item_empty', `enum[${i}] boş ya da yalnız boşluk.`);
          return;
        }
        if (s.length > ENUM_LIMITS.maxItemLength) {
          add('enum_item_too_long', `enum[${i}] çok uzun.`);
          return;
        }
        const k = trLower(s);
        if (seen.has(k)) add('enum_duplicate', `enum listesinde tekrar eden seçenek: «${s}»`);
        seen.add(k);
      });
    }
  } else if (V.enum !== undefined) {
    add('validation_prop_not_applicable', 'enum yalnız enum türünde kullanılabilir.');
  }

  if (V.urlPolicy !== undefined && !Object.hasOwn(URL_POLICIES, String(V.urlPolicy))) {
    add('unknown_url_policy', `bilinmeyen urlPolicy: ${String(V.urlPolicy)}`);
  }
  return errs;
}

/** `validateDefaultValue` — `m01e-schema.js:224`. §1.5, through the same pipeline. */
export function validateDefaultValue(field: FieldLike): SchemaError[] {
  const errs: SchemaError[] = [];
  const key = field?.key || '(anahtarsız)';
  const path = `fieldDefinitions.${key}.defaultValue`;
  if (
    !field ||
    field.defaultValue === undefined ||
    field.defaultValue === null ||
    String(field.defaultValue) === ''
  )
    return errs;
  if (field.auto) {
    errs.push({
      code: 'default_on_auto_field',
      message: `«${key}» auto alanı defaultValue taşıyamaz.`,
      path,
    });
    return errs;
  }
  const probe: FieldLike = { ...field, required: true, defaultValue: undefined };
  let r: ValueResult;
  try {
    r = validateFieldValue(probe, field.defaultValue, { defaultProbe: true });
  } catch (e) {
    errs.push({
      code: 'default_validation_exception',
      message: `«${key}» varsayılan değeri doğrulanamadı: ${e instanceof Error ? e.message : String(e)}`,
      path,
    });
    return errs;
  }
  if (!r.ok) {
    for (const e of r.errors) {
      errs.push({
        code: 'invalid_default_value',
        message: `«${key}» varsayılan değeri kendi alan kurallarını geçmiyor (${e.code}): ${e.message}`,
        path,
      });
    }
  }
  return errs;
}

/** `validateFieldDefinitionSchema` — `m01e-schema.js:249`. */
function validateFieldDefinitionSchema(field: FieldLike, context: { subjectType?: string }): SchemaError[] {
  const errs: SchemaError[] = [];
  const key = field?.key || '(anahtarsız)';
  const add = (code: string, message: string): void => {
    errs.push({ code, message: `«${key}» ${message}`, path: `fieldDefinitions.${key}` });
  };
  if (!field || typeof field !== 'object')
    return [
      { code: 'field_not_object', message: 'FieldDefinition bir nesne değil.', path: 'fieldDefinitions' },
    ];

  if (typeof field.id !== 'string' || !CANONICAL_ID_RX.test(field.id))
    add(
      'invalid_field_id',
      `id canonical biçimde olmalı (küçük harf ile başlar, 2–64 karakter): ${String(field.id)}`,
    );
  if (typeof field.key !== 'string' || !CANONICAL_KEY_RX.test(field.key))
    add('invalid_field_key', `key canonical biçimde olmalı: ${String(field.key)}`);

  const labels = field.labels || {};
  if (typeof labels.tr !== 'string' || !labels.tr.trim())
    add('missing_tr_label', 'labels.tr zorunlu ve boş olamaz.');
  else if (labels.tr.length > LABEL_MAX_LENGTH)
    add('label_too_long', `labels.tr ${LABEL_MAX_LENGTH} karakteri aşamaz.`);
  if (labels.en !== undefined && (typeof labels.en !== 'string' || labels.en.length > LABEL_MAX_LENGTH))
    add('invalid_en_label', 'labels.en string ve 160 karakterden kısa olmalı.');

  if (!FIELD_DATA_TYPES.some((x) => x.key === field.dataType))
    add('invalid_data_type', `dataType allowlist dışında: ${String(field.dataType)}`);
  if (!VISIBILITY.includes(String(field.visibility)))
    add('invalid_visibility', `visibility allowlist dışında: ${String(field.visibility)}`);

  for (const b of ['required', 'auto', 'importable'] as const) {
    if (field[b] !== undefined && typeof field[b] !== 'boolean')
      add('non_boolean_flag', `${b} boolean olmalı (şu an ${typeof field[b]}).`);
  }

  if (field.normalization !== undefined) {
    const own = field.normalization;
    if (!Array.isArray(own) && typeof own !== 'string')
      add('invalid_normalization', 'normalization dizi ya da string olmalı.');
    const list = Array.isArray(own) ? own : [own];
    for (const n of list) {
      if (!Object.hasOwn(NORMALIZERS, String(n)))
        add(
          'unknown_normalizer',
          `bilinmeyen normalizer: ${String(n)} (izinli: ${Object.keys(NORMALIZERS).join(', ')})`,
        );
    }
  }

  /* role / subjectType consistency — only checked when a role is declared. */
  if (field.role !== undefined && field.role !== null && field.role !== '') {
    const known = [...HOLDER_ROLES.person, ...HOLDER_ROLES.organization];
    const extra = [
      'issue_date',
      'expiry_date',
      'credential_title',
      'credential_code',
      'verification_url',
      'program',
      'period',
      'score',
      'level',
      'note',
    ];
    if (!known.concat(extra).includes(field.role))
      add('unknown_field_role', `bilinmeyen role: ${String(field.role)}`);
    if (context.subjectType && known.includes(field.role)) {
      const want = HOLDER_ROLES[context.subjectType === 'organization' ? 'organization' : 'person'];
      if (!want.includes(field.role))
        add('role_subject_mismatch', `role «${field.role}» subjectType=${context.subjectType} ile uyumsuz.`);
    }
  }
  errs.push(...validateValidationConfig(field));
  errs.push(...validateDefaultValue(field));
  return errs;
}

/** `validateFieldAndLayerIds` — `m01e-schema.js:298`. §2, the identity invariants. */
function validateFieldAndLayerIds(layout: EditorLayout): SchemaError[] {
  const errs: SchemaError[] = [];
  const add = (code: string, message: string, path: string): void => {
    errs.push({ code, message, path });
  };
  const defs = layout.fieldDefinitions || [];
  const layers = layout.layers || [];

  const seenFieldIds = new Map<string, number>();
  const seenKeys = new Map<string, number>();
  defs.forEach((f, i) => {
    const id = f?.id;
    if (typeof id === 'string' && id) {
      if (seenFieldIds.has(id))
        add(
          'duplicate_field_id',
          `Alan kimliği tekrar ediyor: «${id}» (${seenFieldIds.get(id)} ve ${i}).`,
          `fieldDefinitions[${i}].id`,
        );
      else seenFieldIds.set(id, i);
    }
    const k = f?.key;
    if (typeof k === 'string' && k) {
      if (seenKeys.has(k))
        add('duplicate_field_key', `Alan anahtarı tekrar ediyor: «${k}».`, `fieldDefinitions[${i}].key`);
      else seenKeys.set(k, i);
    }
  });

  const seenLayerIds = new Map<string, number>();
  layers.forEach((l, i) => {
    const id = l?.id;
    if (typeof id !== 'string' || !CANONICAL_ID_RX.test(id))
      add('invalid_layer_id', `Katman kimliği canonical biçimde değil: ${String(id)}`, `layers[${i}].id`);
    else if (seenLayerIds.has(id))
      add(
        'duplicate_layer_id',
        `Katman kimliği tekrar ediyor: «${id}» (${seenLayerIds.get(id)} ve ${i}).`,
        `layers[${i}].id`,
      );
    else seenLayerIds.set(id, i);
    if (l?.fieldKey && !seenKeys.has(l.fieldKey))
      add(
        'layer_field_missing',
        `Katman «${id}» var olmayan alana bağlı: ${l.fieldKey}`,
        `layers[${i}].fieldKey`,
      );
  });

  const seenSafe = new Map<string, number>();
  (layout.safeAreas || []).forEach((s, i) => {
    const id = s?.id;
    if (!id) return;
    if (seenSafe.has(id))
      add('duplicate_safe_area_id', `Güvenli alan kimliği tekrar ediyor: «${id}».`, `safeAreas[${i}].id`);
    else seenSafe.set(id, i);
  });

  /* Font reference identity: the same family + weight + style may not appear twice. */
  const seenFace = new Map<string, number>();
  const seenFamily = new Map<string, number>();
  const fontRefs = (layout as unknown as { fontReferences?: GateFontReference[] }).fontReferences || [];
  fontRefs.forEach((fr, i) => {
    const fam = fr?.family;
    if (fam) {
      if (seenFamily.has(fam))
        add(
          'duplicate_font_family',
          `Font ailesi referansı tekrar ediyor: «${fam}».`,
          `fontReferences[${i}].family`,
        );
      else seenFamily.set(fam, i);
    }
    (fr?.faces || []).forEach((f, j) => {
      const sig = `${fam}|${f?.weight}|${f?.style}`;
      if (seenFace.has(sig))
        add('duplicate_font_face', `Font yüzü tekrar ediyor: ${sig}.`, `fontReferences[${i}].faces[${j}]`);
      else seenFace.set(sig, i);
    });
  });
  return errs;
}

/** `schemaErrorMessages` — `m01e-schema.js:364`, for the callers that want plain strings. */
const schemaErrorMessages = (errs: SchemaError[]): string[] =>
  errs.map((e) => `${e.message}${e.code ? ` [${e.code}]` : ''}`);

// ------------------------------------------------------------------- §4 holder field invariant

/**
 * `validateHolderFieldInvariant` — `m01c-validate.js:182`.
 *
 * One function, so the checklist, `publishRelease`, project import and issuance cannot disagree
 * about who a credential is made out to.
 */
export function validateHolderFieldInvariant(layout: EditorLayout, opts: { value?: string } = {}): string[] {
  const errs: string[] = [];
  const defs = layout.fieldDefinitions || [];
  const key = layout.holderField;
  if (!key) return ['Sahip alanı (holderField) tanımlı değil.'];
  const f = defs.find((d) => d.key === key);
  if (!f) return [`Sahip alanı «${key}» fieldDefinitions içinde bulunmuyor.`];
  if (f.required !== true)
    errs.push(`Sahip alanı «${key}» required=true olmak zorundadır (şu an ${String(f.required)}).`);
  if (!['string', 'text'].includes(f.dataType))
    errs.push(`Sahip alanı «${key}» string veya text olmalı (şu an ${f.dataType}).`);
  const st = layout.subjectType === 'organization' ? 'organization' : 'person';
  const wantRoles = HOLDER_ROLES[st];
  const roleOk = wantRoles.includes(String(f.role)) || wantRoles.includes(f.key);
  if (!roleOk)
    errs.push(
      `Sahip alanı «${key}» subjectType=${st} ile uyumlu rol taşımıyor (beklenen: ${wantRoles.join(' veya ')}).`,
    );
  if (f.visibility === 'private')
    errs.push(`Sahip alanı «${key}» private olamaz; public metadata ve/veya render için izinli olmalıdır.`);
  const inRender = (layout.layers || []).some(
    (l) => l.fieldKey === key && (l.kind === 'text' || l.kind === 'image'),
  );
  const inMeta = canPublicMetadata(f.visibility);
  if (!inRender && !inMeta)
    errs.push(`Sahip alanı «${key}» ne bir render katmanına bağlı ne de public metadata'da gösteriliyor.`);
  if (inRender && !canRender(f.visibility))
    errs.push(`Sahip alanı «${key}» render katmanına bağlı ama görünürlüğü render'a izin vermiyor.`);
  if (opts.value !== undefined && String(opts.value ?? '').trim() === '')
    errs.push(`Sahip alanı «${key}» boş veya yalnız boşluk olamaz.`);
  return errs;
}

/** `validateFieldSchema` — `m01c-validate.js:207`. Field, layer and visibility consistency. */
export function validateFieldSchema(layout: EditorLayout): string[] {
  const errs: string[] = [];
  const defs = layout.fieldDefinitions || [];
  const keys = new Set<string>();
  /* §1/§2 — the schema, the validation configuration, the default value and id uniqueness all go
     through the same gate. Without this line a broken preset, reversed bounds, an unknown
     normalizer and a duplicate id could all be published. */
  errs.push(
    ...schemaErrorMessages([
      ...defs.flatMap((f) => validateFieldDefinitionSchema(asField(f), { subjectType: layout.subjectType })),
      ...validateFieldAndLayerIds(layout),
    ]),
  );
  for (const f of defs) {
    if (!/^[a-z][a-z0-9_]*$/.test(f.key))
      errs.push(`Alan anahtarı geçersiz: «${f.key}» (küçük harf, rakam ve alt çizgi)`);
    if (keys.has(f.key)) errs.push(`Alan anahtarı tekrar ediyor: ${f.key}`);
    keys.add(f.key);
    if (!VISIBILITY.includes(f.visibility))
      errs.push(`«${f.key}» için görünürlük değeri geçersiz: ${f.visibility}`);
    if (isSystemPrivate(f.key) && f.visibility !== 'private')
      errs.push(`«${f.key}» sistem alanıdır ve private olmak zorundadır`);
    if (f.dataType === 'email' && f.visibility !== 'private')
      errs.push(`E-posta alanı «${f.key}» public olamaz`);
    if (!f.auto && isImageDataType(f.dataType))
      errs.push(
        `«${f.key}» görsel/imza türünde dinamik alan; v1'de desteklenmiyor, Asset Layer olarak ekleyin`,
      );
    if (!f.auto && f.dataType === 'qr')
      errs.push(`«${f.key}» QR alanı auto olmalıdır; dinamik claim olarak tanımlanamaz`);
    if (!f.auto && !isScalarDataType(f.dataType))
      errs.push(`«${f.key}» için desteklenmeyen dinamik veri türü: ${f.dataType}`);
  }
  errs.push(...validateHolderFieldInvariant(layout));
  for (const l of layout.layers || []) {
    if (!l.fieldKey) continue;
    const f = defs.find((d) => d.key === l.fieldKey);
    if (!f) {
      errs.push(`Katman tanımsız alana bağlı: ${l.fieldKey}`);
      continue;
    }
    if (!canRender(f.visibility))
      errs.push(
        `«${fieldLabelOf(asField(f), 'tr')}» alanının görünürlüğü «${VISIBILITY_TR[f.visibility] ?? f.visibility}»; public görsel katmanına bağlanamaz`,
      );
    if (isImageDataType(f.dataType) && l.kind !== 'image')
      errs.push(`«${f.key}» görsel alanı yalnız görsel katmanına bağlanabilir`);
    if (f.dataType === 'qr' && l.kind !== 'qr')
      errs.push(`«${f.key}» QR alanı yalnız QR katmanına bağlanabilir`);
  }
  return errs;
}

// ----------------------------------------------------------------------- §5 the real fit probe

/** `FIT_PROBE_SAMPLES` — `m02-mockdb.js:251`. Three length classes, one plan each. */
const FIT_PROBE_SAMPLES: Record<string, Record<string, string>> = {
  kisa: {
    string: 'Ada Yıl',
    text: 'Kısa',
    number: '7',
    date: '2026-01-05',
    datetime: '2026-01-05T09:00:00Z',
    email: 'a@b.co',
    url: 'https://a.co',
    qr: 'https://a.co',
  },
  orta: {
    string: 'Mehmet Ali Karaduman',
    text: 'Orta uzunlukta örnek metin',
    number: '1250',
    date: '2026-09-02',
    datetime: '2026-09-02T10:00:00Z',
    email: 'mehmet.ali@happyplacetowork.com.tr',
    url: 'https://happyplacetowork.com.tr/c/ABCD-1234',
    qr: 'https://happyplacetowork.com.tr/c/ABCD-1234',
  },
  uzun: {
    string: 'Ayşegül Hüsniye Küçükoğulları-Değirmencioğlu',
    text: 'Uzun örnek metin: bu alan üç satıra kadar taşabilecek uzunlukta bir içerik taşır ve sınırları zorlar.',
    number: '999999999',
    date: '2026-12-31',
    datetime: '2026-12-31T23:59:59Z',
    email: 'ayşegul.husniye.kucukogullari@uzunalanadi-kurumsal.com.tr',
    url: 'https://happyplacetowork.com.tr/c/WXYZ-9876-UZUN',
    qr: 'https://happyplacetowork.com.tr/c/WXYZ-9876-UZUN',
  },
};

function fitProbeData(layout: EditorLayout, sample: Record<string, string>): RenderData {
  const data: RenderData = {};
  for (const f of layout.fieldDefinitions || []) {
    const key = f.dataType && sample[f.dataType] !== undefined ? f.dataType : 'string';
    data[f.key] = sample[key];
  }
  data.verification_url = sample.url;
  return data;
}

export type FitProbeFailure =
  | (PlanFailure & { probe: string })
  | { probe: string; code: 'plan_exception'; message: string };

export interface FitProbeResult {
  ok: boolean;
  probes: number;
  failures: FitProbeFailure[];
  summary: string[];
}

/**
 * `probeLayoutFit` — `m02-mockdb.js:270`.
 *
 * §5 — the fit row is computed from the real render plan, never from UI state. The plan is
 * fail-closed, so overflow, leaving the canvas, an unresolved asset, an unverified font and
 * invalid geometry are all caught here. There is no «accept the overflow» option.
 */
export function probeLayoutFit(layout: EditorLayout): FitProbeResult {
  const names = Object.keys(FIT_PROBE_SAMPLES);
  const failures: FitProbeFailure[] = [];
  const summary: string[] = [];
  for (const n of names) {
    const sample = FIT_PROBE_SAMPLES[n] as Record<string, string>;
    let plan: ReturnType<typeof resolveRenderPlan>;
    try {
      /* A draft is being probed, so it folds by the language it declares — the same language
         `buildReleaseSnapshot` will pin into these layers. A probe that folded differently from the
         publish would measure a string the server never draws. */
      plan = resolveRenderPlan(
        asRenderLayout(layout),
        fitProbeData(layout, sample),
        {},
        {
          strict: false,
          foldLocale: layout.defaultLanguage,
        },
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      summary.push(`${n}: plan hatası (${message})`);
      failures.push({ probe: n, code: 'plan_exception', message });
      continue;
    }
    if (plan.ok) continue;
    for (const f of plan.failures) failures.push({ ...f, probe: n });
    const byCode = [...new Set(plan.failures.map((f) => f.code))];
    summary.push(`${n}: ${byCode.join(', ')} (${plan.failures.length} katman)`);
  }
  return { ok: failures.length === 0, probes: names.length, failures, summary };
}

// --------------------------------------------------------------------- fonts, assets, references

const facesOf = (family: string | undefined): RenderFont[] => allFonts().filter((f) => f.family === family);

type FontStatus = 'loaded' | 'loading' | 'failed' | 'webfont_no_asset' | 'fallback';

/**
 * `evaluateFontReadiness` — `m01b-assets.js:480`, for one family.
 *
 * `document.fonts.check()` alone is not proof: a family only counts as ready when real font asset
 * bytes back it. Everything in the library is registered by `styles/fonts.css` with the
 * reference's own bytes, so a family with faces is `loaded` here for the same reason it is
 * upstream. A family with none falls back to the reference's web-font probe, which needs BOTH the
 * `fonts.check` answer and a measurable metric difference from monospace and serif; when the
 * measurement cannot be taken the answer is `fallback`, which blocks publication.
 */
function fontReadiness(family: string | undefined): { status: FontStatus } | null {
  if (!family) return null;
  const faces = facesOf(family);
  if (faces.length) return { status: 'loaded' };
  let checked = false;
  try {
    checked = document.fonts?.check(`600 20px "${family}"`) ?? false;
  } catch {
    checked = false;
  }
  let differs = false;
  try {
    const cv = document.createElement('canvas').getContext('2d');
    if (cv) {
      const S = 'HPWİğüşçö 0123456789';
      cv.font = `600 48px "${family}", monospace`;
      const a = cv.measureText(S).width;
      cv.font = '600 48px monospace';
      const b = cv.measureText(S).width;
      cv.font = `600 48px "${family}", serif`;
      const c = cv.measureText(S).width;
      differs = Math.abs(a - b) > 0.5 && Math.abs(a - c) > 0.5;
    }
  } catch {
    differs = false;
  }
  return { status: checked && differs ? 'webfont_no_asset' : 'fallback' };
}

export interface GateFontFace {
  fontAssetId: string;
  sha256: string;
  weight: number;
  style: string;
}

export interface GateFontReference {
  family: string;
  faces: GateFontFace[];
  assurance: 'asset_verified' | 'webfont_unverified' | 'fallback';
}

/** One text layer's typographic need: the exact face it asks the library for. */
export interface LayerFontNeed {
  /** The layer's own id, so a screen can name or select it. */
  layerKey: string;
  /** What the layer is called in the editor's own list. */
  layerName: string;
  family: string;
  weight: number;
  style: string;
}

export interface FontResolution {
  /** One reference per family, each carrying every DISTINCT face its layers really resolved to. */
  references: GateFontReference[];
  /** Needs no registered face answers. Never silently mapped onto a different weight. */
  missing: LayerFontNeed[];
}

const needStyle = (style: unknown): string => (style === 'italic' ? 'italic' : 'normal');

/** How a person recognises the layer in the editor's layer list. */
const needName = (l: { id?: string; name?: string; fieldKey?: string }): string =>
  (l.name ?? '').trim() || (l.fieldKey ?? '').trim() || (l.id ?? '').trim() || 'Metin katmanı';

/**
 * THE font resolution — one rule, used by the checklist, the saved draft and the publish request.
 *
 * It used to be `faces.find((f) => f.weight === w) ?? faces[0]`, and that single `??` produced two
 * separate wrongs at once. A weight nobody uploaded was silently bound to whatever face happened to
 * be first, so a layer the author set to 600 went out drawn in 400 with nothing said. And when two
 * weights both fell to that same first face, the reference carried the SAME face twice — which the
 * backend refuses outright (`domain/template/release.ts`, `duplicate_font_face`) while this
 * checklist was still showing the font as verified. The author was told everything was fine and the
 * publish came back rejected.
 *
 * So a face now matches on family, weight AND style, exactly, or it does not match at all:
 *
 *   · every need a real face answers is collected, DEDUPLICATED BY FACE — two layers sharing one
 *     face produce one reference entry, which is what makes the request valid;
 *   · every need nothing answers is returned in `missing`, named by layer, so the gate can say
 *     which layer wants which family/weight/style rather than hiding it behind a substitute.
 *
 * Deduplicating alone would have made the request legal and left the missing weight invisible; that
 * is exactly the half-fix this avoids. Both halves are reported.
 */
export function resolveLayoutFonts(d: EditorLayout): FontResolution {
  const needs: LayerFontNeed[] = [];
  for (const l of d.layers || []) {
    if (l.kind !== 'text' && l.kind !== 'static') continue;
    if (!l.font) continue;
    needs.push({
      layerKey: String(l.id ?? ''),
      layerName: needName(l as { id?: string; name?: string; fieldKey?: string }),
      family: String(l.font),
      weight: Number(l.weight),
      style: needStyle((l as { style?: unknown }).style),
    });
  }

  const missing: LayerFontNeed[] = [];
  const references: GateFontReference[] = [];

  for (const family of layoutFonts(asRenderLayout(d))) {
    if (!family) continue;
    const name = String(family);
    const faces = facesOf(name);
    const mine = needs.filter((n) => n.family === name);
    /* Keyed by the face's own id: the same face asked for by two layers is one entry. */
    const picked = new Map<string, RenderFont>();
    for (const need of mine) {
      const face = faces.find((f) => f.weight === need.weight && f.style === need.style);
      if (face) picked.set(face.id, face);
      else missing.push(need);
    }
    if (picked.size) {
      references.push({
        family: name,
        faces: [...picked.values()]
          /* A stable order, so the same design produces the same request every time. */
          .sort((a, b) => a.weight - b.weight || a.style.localeCompare(b.style))
          .map((f) => ({ fontAssetId: f.id, sha256: f.sha256, weight: f.weight, style: f.style })),
        assurance: 'asset_verified',
      });
      continue;
    }
    const st = fontReadiness(name);
    references.push({
      family: name,
      faces: [],
      assurance: st && st.status === 'webfont_no_asset' ? 'webfont_unverified' : 'fallback',
    });
  }

  return { references, missing };
}

/** `buildFontReferences` — `m02-mockdb.js:322`. One reference per family, with its real faces. */
export function buildFontReferences(d: EditorLayout): GateFontReference[] {
  return resolveLayoutFonts(d).references;
}

/** «Ad (Poppins 600 normal)» — how a missing need is named to the author. */
export const describeFontNeed = (n: LayerFontNeed): string =>
  `${n.layerName} (${n.family} ${n.weight}${n.style === 'italic' ? ' italik' : ''})`;

interface GateAssetRef {
  assetId?: string | undefined;
  sha256?: string | undefined;
}

/** `collectAssetRefs` — `m02-mockdb.js:316`: the background plus every image layer. */
function collectAssetRefs(d: EditorLayout): GateAssetRef[] {
  const out: GateAssetRef[] = [];
  const bg = (d.background as { assetRef?: GateAssetRef | null } | null)?.assetRef;
  if (bg) out.push(bg);
  for (const l of d.layers || []) if (l.assetRef) out.push(l.assetRef);
  return out;
}

// ------------------------------------------------------------------------------ the checklist

export interface ChecklistItem {
  /** Upstream's own key; `sync` in the modal keys off `ack` by name. */
  key: string;
  label: string;
  ok: boolean;
  /** Shown under the label as `.small.muted`; empty when the row has nothing to add. */
  detail: string;
  /** A failing blocking item stops publication; a failing non-blocking one is only a warning. */
  blocking: boolean;
}

export interface ReleaseChecklist {
  items: ChecklistItem[];
  /** Every BLOCKING item passed. A failing warning row does not gate publication. */
  ok: boolean;
}

/**
 * `releaseChecklist` — `m02-mockdb.js:285`, the single source of «may this draft be published».
 *
 * The order is upstream's and is load-bearing: it is the order the rows are drawn in.
 */
export function releaseChecklist(t: DemoTemplate, d: EditorLayout): ReleaseChecklist {
  const items: ChecklistItem[] = [];
  const add = (key: string, label: string, ok: unknown, detail?: string, blocking = true): void => {
    items.push({ key, label, ok: !!ok, detail: detail || '', blocking });
  };
  const md = d.localizedMetadata || {};

  add(
    'metadata',
    'Release metadata (TR başlık ve açıklama)',
    !!(md.tr?.title && md.tr?.description),
    md.tr?.title ? '' : 'TR başlık/açıklama boş',
  );
  add(
    'policy',
    'Publication policy seçildi',
    PUBLICATION_POLICIES.some((p) => p.key === d.publicationPolicy),
    d.publicationPolicy || '—',
  );

  const orgConflict = d.subjectType === 'organization' && d.permissions.allowLinkedInProfile;
  add(
    'subject',
    'Subject type ve izinler tutarlı',
    !orgConflict,
    orgConflict
      ? 'Kurum kaydında LinkedIn profile-add açık olamaz'
      : `${d.subjectType} · profil ekleme ${d.permissions.allowLinkedInProfile ? 'açık' : 'kapalı'}`,
  );

  const refs = collectAssetRefs(d);
  const badReasons = refs
    .map((r) => resolveAssetRef(r))
    .filter((x) => !x.ok)
    .map((x) => ('reason' in x ? x.reason : 'unknown'));
  add(
    'assets',
    'Arka plan ve tüm görsel referansları digest ile eşleşiyor',
    badReasons.length === 0,
    badReasons.length ? badReasons.join(', ') : `${refs.length} referans doğrulandı`,
  );

  const fams = layoutFonts(asRenderLayout(d));
  const fontAssets = fams.every((f) => facesOf(f).length);
  const fallback = fams.filter((f) => {
    const st = fontReadiness(f);
    return !st || st.status === 'fallback';
  });
  /*
   * A weight nobody uploaded is a BLOCKING answer, and it names the layers.
   *
   * It used to be invisible: the binder quietly bound the missing weight to another face, this row
   * read «doğrulandı», and the backend then refused the release — for `duplicate_font_face` when two
   * weights had been folded onto the same face, or by drawing something the author never chose when
   * they had not. The same resolution the request and the draft use decides this row, so the three
   * can no longer disagree.
   */
  const missingFaces = resolveLayoutFonts(d).missing;
  add(
    'fonts',
    'Font durumu',
    fallback.length === 0 && missingFaces.length === 0,
    fallback.length
      ? /* The same distinction the editor's own pill draws: a family with NO registered face is an
           installation whose font library is still empty (the binaries are uploaded, they do not
           ship — ADR-003), which is fixed in «Varlıklar → Font yükle»; a family whose registered
           faces would not load is a different problem. The row blocks either way. */
        fallback.every((f) => facesOf(f).length === 0)
        ? `Kayıtlı font asset'i yok: ${fallback.join(', ')} — canonical export mümkün değil`
        : `Fallback font: ${fallback.join(', ')} — canonical export mümkün değil`
      : missingFaces.length
        ? `Yüklü olmayan font yüzü: ${missingFaces.map(describeFontNeed).join(' · ')} — bu kalınlığı «Varlıklar → Font yükle» ile kaydedin veya katmanın kalınlığını kayıtlı bir değere getirin`
        : fontAssets
          ? "Gömülü font asset digest'leriyle doğrulandı"
          : 'Web fontu yüklü; canonical export için font asset yükleyin',
  );

  const schemaErrs = validateFieldSchema(d);
  add(
    'schema',
    'Alan şeması ve katman görünürlüğü geçerli',
    schemaErrs.length === 0,
    schemaErrs.slice(0, 3).join(' · '),
  );

  const hf = holderFieldKey(asRenderLayout(d));
  const holderErrs = validateHolderFieldInvariant(d);
  add(
    'holder',
    "Sahip alanı invariant'ı (required + tür + rol + görünürlük + render/metadata)",
    holderErrs.length === 0,
    holderErrs.length ? holderErrs.slice(0, 2).join(' · ') : `${hf} · required · ${d.subjectType}`,
  );

  const fitProbe = probeLayoutFit(d);
  add(
    'fit',
    'Bütün örnek uzunluklarda yerleşim geçerli (taşma / tuval dışı / asset / font)',
    fitProbe.ok,
    fitProbe.ok ? `${fitProbe.probes} örnek uzunluk · ihlal yok` : fitProbe.summary.slice(0, 4).join(' · '),
    true,
  );

  /* `__previewChecked` is written true in exactly one place in the frozen artifact — the demo
     seed's `publishSeedRelease` (`m02b-seed.js:222`) — and `publishRelease` clears it again on
     every publication. Nothing in the editor sets it, so for a seeded template this row stays ✕
     and the publish button never enables. That is the reference's behaviour, reproduced. */
  add(
    'preview',
    'Public / e-posta / LinkedIn önizlemesi incelendi',
    !!t.__previewChecked,
    t.__previewChecked ? '' : 'Editörde «Önizlemeleri incele» adımını çalıştırın',
  );
  add(
    'ack',
    'Yönetici açık yayın onayı',
    !!t.__publishAck,
    t.__publishAck ? '' : 'Yayınla penceresinde onay kutusu işaretlenmeli',
  );

  return { items, ok: items.filter((i) => i.blocking).every((i) => i.ok) };
}

/**
 * `collectOverflow` — `m08-admin.js:138`.
 *
 * Ported for completeness and exported, but the modal does not call it: upstream stashes the list
 * on `lay.__overflow` (`m08-admin.js:116`) and the only other mention of that property is a
 * `delete` on an object that never had it (`m02-mockdb.js:358`). Nothing reads it and nothing
 * renders it, so calling it would buy three extra render-plan resolutions and not one pixel. The
 * blocking overflow answer the gate actually shows comes from `probeLayoutFit`.
 */
export function collectOverflow(t: DemoTemplate, lay: EditorLayout, publicBase?: string): string[] {
  const out: string[] = [];
  const W = canvasW(lay.canvas);
  const H = canvasH(lay.canvas);
  for (const mode of ['short', 'long', 'org'] as SampleMode[]) {
    const layout = asRenderLayout(lay);
    const plan = resolveRenderPlan(layout, sampleData(t, mode, layout, { publicBase }), {
      width: W,
      height: H,
    });
    for (const it of plan.items || []) {
      const name = it.layer?.fieldKey || it.layer?.id || '?';
      if (it.kind === 'text') {
        if (it.overflow) out.push(`${mode}: «${name}» metni minimum puntoda bile sığmıyor`);
        const g = it.geo as unknown as { x: number; top: number; blockH: number };
        const width = it.width || 0;
        const left =
          it.layer.align === 'left' ? g.x : it.layer.align === 'right' ? g.x - width : g.x - width / 2;
        if (left < -1 || left + width > W + 1 || g.top < -1 || g.top + g.blockH > H + 1)
          out.push(`${mode}: «${name}» canvas dışına taşıyor`);
      } else if (it.kind === 'image' || it.kind === 'qr') {
        const box = it.geo as unknown as
          | { x: number; y: number; w?: number; h?: number; size?: number }
          | undefined;
        const g = box
          ? box
          : it.placement
            ? { x: it.placement.dx, y: it.placement.dy, w: it.placement.dw, h: it.placement.dh }
            : null;
        if (
          g &&
          (g.x < -1 || g.y < -1 || g.x + (g.w ?? g.size ?? 0) > W + 1 || g.y + (g.h ?? g.size ?? 0) > H + 1)
        )
          out.push(`${mode}: «${name}» canvas dışına taşıyor`);
        if (it.missing) out.push(`${mode}: «${name}» görsel referansı çözülemedi`);
      }
    }
  }
  return [...new Set(out)];
}

// ------------------------------------------------------------------------------- publication

const issuerProfileSnapshot = (s: DemoSettings): DemoRelease['issuerProfileSnapshot'] => ({
  displayName: s.issuerDisplayName,
  linkedinIssuerName: s.linkedinIssuerName,
  linkedinPageUrl: s.linkedinPageUrl,
  website: s.issuerWebsite,
  supportEmail: s.supportEmail,
  publicBase: s.publicBase,
});

/** `sha256Canonical` — `m01-utils.js:84`, over the canonical JSON the render engine already writes. */
async function sha256Canonical(obj: unknown): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return 'nocrypto';
  const bytes = new TextEncoder().encode(canonicalJSON(obj));
  const digest = await subtle.digest('SHA-256', bytes.buffer as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** `deepFreeze` — `m01-utils.js:42`. */
function deepFreeze<T>(o: T): T {
  if (o === null || typeof o !== 'object' || Object.isFrozen(o)) return o;
  Object.freeze(o);
  for (const k of Object.getOwnPropertyNames(o)) {
    const v = (o as Record<string, unknown>)[k];
    if (v && typeof v === 'object') deepFreeze(v);
  }
  return o;
}

export interface PublishSuccess {
  ok: true;
  release: DemoRelease;
  /** The stripped draft the release was cut from; `applyRelease` writes it back as `t.draft`. */
  draft: EditorLayout;
  at: string;
}

export interface PublishFailure {
  ok: false;
  problems: string[];
  checklist?: ChecklistItem[];
}

export type PublishResult = PublishSuccess | PublishFailure;

export interface PublishOptions {
  settings: DemoSettings;
  at?: string;
  allowArchived?: boolean;
}

/**
 * `publishRelease` — `m02-mockdb.js:333`, split in two.
 *
 * This half is everything before the write: the domain guards, the checklist gate and the release
 * hash, which is `await`ed and therefore cannot run inside the provider's synchronous mutation.
 * `releaseHash` is computed BEFORE anything is stored (fail-closed): a release that could not be
 * hashed is not a release. The write is `applyRelease` below.
 */
export async function buildRelease(
  t: DemoTemplate,
  layout: EditorLayout,
  note: string,
  opts: PublishOptions,
): Promise<PublishResult> {
  const d = structuredClone(layout);
  if (t.status === 'archived' && !opts.allowArchived)
    return { ok: false, problems: ['Arşivdeki şablon yayınlanamaz; önce arşivden çıkarın.'] };
  if (d.subjectType === 'organization' && d.permissions.allowLinkedInProfile)
    return {
      ok: false,
      problems: ['Kurum kaydında LinkedIn profile-add izni açık olamaz (domain invariant).'],
    };
  const schemaErrs = validateFieldSchema(d);
  if (schemaErrs.length) return { ok: false, problems: schemaErrs };
  for (const r of collectAssetRefs(d)) {
    const res = resolveAssetRef(r);
    if (!res.ok)
      return { ok: false, problems: [`Görsel referansı doğrulanamadı (${res.reason}): ${r.assetId}`] };
  }
  if (!(d.layers || []).some((l) => l.kind === 'text' || l.kind === 'static'))
    return { ok: false, problems: ['Yayınlamak için en az bir metin katmanı gerekir.'] };
  const fontReferences = buildFontReferences(d);
  if (fontReferences.some((f) => f.assurance === 'fallback'))
    return {
      ok: false,
      problems: [
        `Fallback fontla yayın yapılamaz: ${fontReferences
          .filter((f) => f.assurance === 'fallback')
          .map((f) => f.family)
          .join(', ')}`,
      ],
    };

  /* The one publish gate: no release is cut until every BLOCKING row passes. Calling this
     function past the UI is not enough either — the checklist runs again here. */
  const gate = releaseChecklist(t, d);
  const blocking = gate.items.filter((i) => i.blocking && !i.ok);
  if (blocking.length)
    return {
      ok: false,
      problems: blocking.map((i) => `${i.label}${i.detail ? ` — ${i.detail}` : ''}`),
      checklist: gate.items,
    };

  const at = opts.at || prototypeNowUTC();
  const releaseNumber = (t.releases || []).length + 1;
  const rel = {
    ...d,
    id: `${t.id}-r${releaseNumber}`,
    templateId: t.id,
    releaseNumber,
    releaseSchemaVersion: RELEASE_SCHEMA_VERSION,
    releasedAt: at,
    releaseNote: note || (releaseNumber === 1 ? 'İlk yayın' : 'Düzenleme yayınlandı'),
    templateCode: t.code,
    templateName: t.name,
    issuerProfileSnapshot: issuerProfileSnapshot(opts.settings),
    fontReferences,
    renderProfile: {
      ...d.renderProfile,
      fontAssurance: fontReferences.every((f) => f.assurance === 'asset_verified')
        ? 'asset_verified'
        : 'webfont_unverified',
    },
  } as unknown as DemoRelease;
  rel.releaseHash = await sha256Canonical({ ...rel, releaseHash: undefined });
  if (!/^[0-9a-f]{64}$/.test(rel.releaseHash))
    return { ok: false, problems: ['releaseHash hesaplanamadı; yayın iptal edildi.'] };
  /* `deepFreeze` is the reference's; the demo provider deep-clones the database on every write, so
     what actually keeps a release immutable is rule 3 — nothing ever edits one. */
  return { ok: true, release: deepFreeze(rel), draft: d, at };
}

/**
 * The write half of `publishRelease` (`m02-mockdb.js:359-364`), to be called inside the demo
 * provider's mutation. It APPENDS a release and moves `currentReleaseId` forward; it never edits
 * an existing one, so credentials issued from an earlier release keep their own snapshot
 * (rules 3 and 4).
 */
export function applyRelease(t: DemoTemplate, res: PublishSuccess): void {
  t.releases.push(res.release);
  t.currentReleaseId = res.release.id;
  t.status = 'published';
  t.draft = res.draft as unknown as DemoTemplate['draft'];
  t.updatedAt = res.at;
  t.userModified = true;
  t.__publishAck = false;
  t.__previewChecked = false;
}
